import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAccountStatus } from "@/hooks/useAccountStatus";
import { AccountStatusBanner } from "./AccountStatusBanner";

const DANGER_ZONE_PATH = "/account/danger-zone";

/**
 * Enforces account-status access rules on top of normal auth.
 * Renders inside <AuthRoute>, after the user is confirmed signed in.
 *
 * Rules:
 * - active            → no-op
 * - on_hold           → allow, show banner
 * - deactivated       → allow only /account/*; show reactivate banner
 * - pending_deletion  → allow only /account/danger-zone; show cancel banner
 * - suspended         → force sign-out, redirect to /auth?reason=suspended
 * - anonymized        → force sign-out, redirect to /auth?reason=anonymized
 */
export function AccountStatusGate({ children }: { children: React.ReactNode }) {
  const { data: info, isLoading } = useAccountStatus();
  const location = useLocation();
  const navigate = useNavigate();

  const status = info?.status ?? "active";

  useEffect(() => {
    if (isLoading || !info) return;

    if (status === "suspended" || status === "anonymized") {
      void supabase.auth.signOut().finally(() => {
        navigate(`/auth?reason=${status}`, { replace: true });
      });
      return;
    }

    if (status === "pending_deletion" && location.pathname !== DANGER_ZONE_PATH) {
      navigate(DANGER_ZONE_PATH, { replace: true });
      return;
    }

    if (status === "deactivated" && !location.pathname.startsWith("/account/")) {
      navigate(DANGER_ZONE_PATH, { replace: true });
    }
  }, [status, isLoading, info, location.pathname, navigate]);

  // Hard blocks: render nothing while we redirect
  if (status === "suspended" || status === "anonymized") return null;
  if (status === "pending_deletion" && location.pathname !== DANGER_ZONE_PATH) return null;
  if (status === "deactivated" && !location.pathname.startsWith("/account/")) return null;

  return (
    <>
      {status !== "active" ? <AccountStatusBanner info={info} /> : null}
      {children}
    </>
  );
}
